import { Formik } from "formik";
import { get, isEmpty } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdArrowForwardIos,
  MdInfoOutline,
  MdOutlineApartment,
  MdOutlineBadge,
  MdOutlineCall,
  MdOutlineCalendarToday,
  MdOutlineEmail,
  MdOutlineGroups,
  MdOutlineLocationOn,
  MdOutlineSearch,
  MdOutlineWorkOutline,
} from "react-icons/md";
import AutoComplete from "shared/components/AutoComplete";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import {
  bindInputProps,
  bindTextFieldInputProps,
  convertToTouched,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import InfoBox from "../Components/InfoBox";
import Section from "../Components/Section";
import validation from "./validation";

const companyFromSearchResult = (result) => ({
  siret: get(result, "siret", null),
  siren: get(result, "siren", null),
  legalName: get(result, "legalName", null),
  legalForm: get(result, "legalForm", null),
  street: get(result, "address.street", null),
  zipCode: get(result, "address.zipCode", null),
  city: get(result, "address.city", null),
  phone: null,
  email: null,
  nafCode: get(result, "nafCode", null),
  headcountTier: get(result, "headcountTier", null),
  ccn: get(result, "mainActivity", null),
  idcc: get(result, "idcc", []),
  creationDate: get(result, "creationDate", null),
});

const View = ({
  translation: T,
  initialValues,
  onSubmit,
  nextLoading,
  searchResults,
  onSearch,
  disabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const validationSchema = useMemo(
    () => validation(t("VALIDATIONS", { returnObjects: true })),
    [t],
  );

  const options = useMemo(
    () =>
      searchResults.map((result) => ({
        value: result.siret,
        label: `${result.legalName} — ${result.siret}`,
        result,
      })),
    [searchResults],
  );

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({
        values,
        handleSubmit,
        setValues,
        setFieldValue,
        isValid,
        ...formProps
      }) => {
        const selected = !isEmpty(get(values, "company.siret"));
        return (
          <div className="ac-collectif contact-form">
            <div className="ac-collectif__search contact-form__content">
              <Section
                title={T.COMPANY.SEARCH.TITLE}
                description={T.COMPANY.SEARCH.DESCRIPTION}
              >
                <AutoComplete
                  label={T.COMPANY.SEARCH.LABEL}
                  icon={<MdOutlineSearch size={22} />}
                  placeholder={T.COMPANY.SEARCH.PLACEHOLDER}
                  options={options}
                  onSearch={onSearch}
                  onSelect={(_, option) =>
                    setValues({
                      company: companyFromSearchResult(option.result),
                    })
                  }
                  notFoundContent={T.COMPANY.SEARCH.EMPTY}
                  size="large"
                  disabled={disabled}
                />
                <InfoBox
                  icon={<MdInfoOutline size={24} />}
                  title={T.COMPANY.ONE_CONTRACT_PER_SITE.TITLE}
                  description={T.COMPANY.ONE_CONTRACT_PER_SITE.DESCRIPTION}
                />
              </Section>
            </div>

            {selected && (
              <div className="ac-collectif__sheet contact-form__content mb-16">
                <Section
                  title={T.COMPANY.SECTIONS.IDENTIFICATION.TITLE}
                  description={T.COMPANY.SECTIONS.IDENTIFICATION.DESCRIPTION}
                >
                  <Input
                    disabled
                    label={T.COMPANY.FORM.SIRET.LABEL}
                    icon={<MdOutlineApartment size={22} />}
                    {...bindInputProps({
                      name: "company.siret",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    disabled
                    label={T.COMPANY.FORM.SIREN.LABEL}
                    icon={<MdOutlineApartment size={22} />}
                    {...bindInputProps({
                      name: "company.siren",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    disabled={disabled}
                    label={T.COMPANY.FORM.LEGAL_NAME.LABEL}
                    placeholder={T.COMPANY.FORM.LEGAL_NAME.PLACEHOLDER}
                    icon={<MdOutlineBadge size={22} />}
                    {...bindTextFieldInputProps({
                      name: "company.legalName",
                      values,
                      setFieldValue,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    disabled={disabled}
                    label={T.COMPANY.FORM.LEGAL_FORM.LABEL}
                    placeholder={T.COMPANY.FORM.LEGAL_FORM.PLACEHOLDER}
                    icon={<MdOutlineWorkOutline size={22} />}
                    {...bindInputProps({
                      name: "company.legalForm",
                      values,
                      ...formProps,
                    })}
                  />
                </Section>

                <Section
                  title={T.COMPANY.SECTIONS.CONTACT_DETAILS.TITLE}
                  description={T.COMPANY.SECTIONS.CONTACT_DETAILS.DESCRIPTION}
                >
                  <Input
                    required
                    disabled={disabled}
                    label={T.COMPANY.FORM.STREET.LABEL}
                    placeholder={T.COMPANY.FORM.STREET.PLACEHOLDER}
                    icon={<MdOutlineLocationOn size={22} />}
                    {...bindInputProps({
                      name: "company.street",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    disabled={disabled}
                    label={T.COMPANY.FORM.ZIP_CODE.LABEL}
                    placeholder={T.COMPANY.FORM.ZIP_CODE.PLACEHOLDER}
                    icon={<MdOutlineLocationOn size={22} />}
                    {...bindInputProps({
                      name: "company.zipCode",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    disabled={disabled}
                    label={T.COMPANY.FORM.CITY.LABEL}
                    placeholder={T.COMPANY.FORM.CITY.PLACEHOLDER}
                    icon={<MdOutlineLocationOn size={22} />}
                    {...bindInputProps({
                      name: "company.city",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    disabled={disabled}
                    type="custom"
                    stripSpaces
                    inputMode="numeric"
                    maxLength={10}
                    label={T.COMPANY.FORM.PHONE.LABEL}
                    placeholder={T.COMPANY.FORM.PHONE.PLACEHOLDER}
                    icon={<MdOutlineCall size={22} />}
                    {...bindInputProps({
                      name: "company.phone",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    disabled={disabled}
                    type="email"
                    label={T.COMPANY.FORM.EMAIL.LABEL}
                    placeholder={T.COMPANY.FORM.EMAIL.PLACEHOLDER}
                    icon={<MdOutlineEmail size={22} />}
                    {...bindInputProps({
                      name: "company.email",
                      values,
                      ...formProps,
                    })}
                  />
                </Section>

                <Section
                  title={T.COMPANY.SECTIONS.ACTIVITY.TITLE}
                  description={T.COMPANY.SECTIONS.ACTIVITY.DESCRIPTION}
                >
                  <Input
                    disabled={disabled}
                    label={T.COMPANY.FORM.NAF_CODE.LABEL}
                    placeholder={T.COMPANY.FORM.NAF_CODE.PLACEHOLDER}
                    icon={<MdOutlineWorkOutline size={22} />}
                    {...bindInputProps({
                      name: "company.nafCode",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    disabled
                    label={T.COMPANY.FORM.HEADCOUNT_TIER.LABEL}
                    icon={<MdOutlineGroups size={22} />}
                    {...bindInputProps({
                      name: "company.headcountTier",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    disabled={disabled}
                    label={T.COMPANY.FORM.CCN.LABEL}
                    placeholder={T.COMPANY.FORM.CCN.PLACEHOLDER}
                    icon={<MdOutlineWorkOutline size={22} />}
                    {...bindInputProps({
                      name: "company.ccn",
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    disabled
                    label={T.COMPANY.FORM.IDCC.LABEL}
                    icon={<MdOutlineWorkOutline size={22} />}
                    value={get(values, "company.idcc", []).join(", ")}
                    name="company.idcc"
                  />
                  <Input
                    disabled
                    label={T.COMPANY.FORM.CREATION_DATE.LABEL}
                    icon={<MdOutlineCalendarToday size={22} />}
                    {...bindInputProps({
                      name: "company.creationDate",
                      values,
                      ...formProps,
                    })}
                  />
                </Section>
              </div>
            )}

            {selected && (
              <div className="ac-collectif__secondary-actions">
                <Button
                  type="secondary--link"
                  disabled={disabled}
                  onClick={() => setValues(initialValues)}
                >
                  {T.ACTIONS.CANCEL}
                </Button>
                <Button
                  type="secondary--link"
                  disabled={disabled}
                  onClick={() => setValues({ company: {} })}
                >
                  {T.ACTIONS.CHANGE_COMPANY}
                </Button>
              </div>
            )}

            <ActionButtons
              onSubmit={async () => {
                await formProps.setTouched(
                  convertToTouched(formProps.errors),
                  true,
                );
                if (isValid) handleSubmit();
              }}
              loading={nextLoading}
              disabled={{ next: nextLoading || !selected || disabled }}
              icons={{ next: <MdArrowForwardIos size={16} /> }}
              translation={T.ACTIONS}
            />
          </div>
        );
      }}
    </Formik>
  );
};

export default View;
